<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <FormInput
              v-model="rawStatisticPointTemplate.name"
              :error="errors?.name"
              class="col-start-1 col-end-2"
              type="text"
              :label="$t('statisticPointTemplate.name')"
              :placeholder="$t('statisticPointTemplate.name')"
            />
            <Multiselect
              v-model="rawStatisticPointTemplate.region"
              :error="errors?.region"
              class="col-start-1 col-end-2"
              :options="regions"
              :label="$t('statisticPointTemplate.region')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
              @select="setMapCenter()"
            />
            <LocationMap ref="map" v-model="rawStatisticPointTemplate.gisLocation" v-model:radius="rawStatisticPointTemplate.radius" />
            <div class="flex gap-x-5">
              <FormInput
                :model-value="rawStatisticPointTemplate.gisLocation?.lat"
                :error="errors?.gisLocation?.lat"
                class="w-1/2"
                type="text"
                :label="$t('lat')"
                pattern="^\d*\.?\d*$"
                @update:model-value="setCoords(parseFloat($event), rawStatisticPointTemplate.gisLocation?.long)"
              />
              <FormInput
                :model-value="rawStatisticPointTemplate.gisLocation?.long"
                :error="errors?.gisLocation?.long"
                class="w-1/2"
                type="text"
                :label="$t('long')"
                pattern="^\d*\.?\d*$"
                @update:model-value="setCoords(rawStatisticPointTemplate.gisLocation?.lat, parseFloat($event))"
              />
            </div>
            <FormInput
              v-model="rawStatisticPointTemplate.radius"
              :error="errors?.radius"
              class="col-start-1 col-end-2"
              type="number"
              :label="$t('statisticPointTemplate.radius')"
              :placeholder="$t('statisticPointTemplate.radius')"
            />
            <div class="grid grid-cols-2 gap-x-5">
              <DatePicker
                v-model="rawStatisticPointTemplate.validityStart"
                :error="errors?.validityStart"
                mode="datetime"
                :label="$t('statisticPointTemplate.validityStart')"
              />
              <DatePicker
                v-model="rawStatisticPointTemplate.validityEnd"
                :error="errors?.validityEnd"
                mode="datetime"
                :label="$t('statisticPointTemplate.validityEnd')"
              />
            </div>
          </div>
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {computed, reactive, ref, watch} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {date, number, object, string} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import Multiselect from '@utils/Multiselect.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import InputDuration from '@utils/inputs/InputDuration.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import type {Campaign, CampaignGroup, StatisticPointTemplate, Region} from '@/entities/Entities';

interface Props {
  open: boolean;
  campaignGroup?: CampaignGroup;
  statisticPointTemplate?: StatisticPointTemplate;
}

const emit = defineEmits(['update:open', 'update:statisticPointTemplate', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalStatisticPointTemplate = useVModel(props, 'statisticPointTemplate', emit);

const isNew = computed(() => !props.statisticPointTemplate);
const tKey = computed(() =>
  (isNew.value
    ? 'pages.templates.statisticPointTemplatesList.modal.new'
    : 'pages.templates.statisticPointTemplatesList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const initialData = (statisticPointTemplate?: StatisticPointTemplate) =>
  (statisticPointTemplate
    ? {
      ...statisticPointTemplate,
      region: statisticPointTemplate.region['@id'],
    }
    : {
      region: undefined,
      name: undefined,
      radius: undefined,
      threshold: undefined,
      cardinality: undefined,
      paymentAmount: undefined,
      numOfPhotos: undefined,
      gisLocation: undefined,
      validityStart: undefined,
      validityEnd: undefined,
    });

const rawStatisticPointTemplate = syncRef(internalStatisticPointTemplate, (statisticPointTemplate) => ({
  ...initialData(statisticPointTemplate),
}));

const fetchRegions = collOptionsProvider<Region>('/regions', (item) => ({
  label: item.name,
  value: item['@id'],
  gisLocation: item.gisLocation,
}));

const regions = ref<{ label: string, value: string, gisLocation?: { lat: number, long: number } }[]>([]);
const loadRegions = async () => {
  regions.value = await fetchRegions();
};

loadRegions();

const isPrice = (value: undefined|number) => {
  if (value !== undefined) {
    return /^\d+(\.\d{0,2})?$/.test(value.toString());
  }
  return true;
};

const {
  data: statisticPointTemplate,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    region: string().nullable().required(t('validation.notEmpty')),
    name: string().min(1, t('validation.notEmpty')).nullable().required(t('validation.notEmpty')),
    radius: number()
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty')),
    gisLocation: object({
      lat: number().nullable().required(t('validation.notEmpty')),
      long: number().nullable().required(t('validation.notEmpty')),
    }),
    validityStart: date().nullable(),
    validityEnd: date().nullable(),
  }),
  rawStatisticPointTemplate,
);

function resetData() {
  rawStatisticPointTemplate.value = { ...initialData() } as unknown as typeof statisticPointTemplate['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalStatisticPointTemplate.value = undefined;
}

const map = ref();

const setMapCenter = () => {
  const gisLocation = regions.value.find((region) => region.value === rawStatisticPointTemplate.value?.region)?.gisLocation;
  map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
};

const setCoords = (lat: number|undefined, long: number|undefined) => {
  rawStatisticPointTemplate.value.gisLocation = {
    lat: lat || 0,
    long: long || 0,
  };
};

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.statisticPointTemplate;
    const url = edit ? props.statisticPointTemplate['@id'] : '/statistic_point_templates';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: statisticPointTemplate.value.name,
        region: statisticPointTemplate.value.region,
        radius: statisticPointTemplate.value.radius,
        gisLocation: statisticPointTemplate.value.gisLocation,
        validityStart: statisticPointTemplate.value.validityStart,
        validityEnd: statisticPointTemplate.value.validityEnd,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
